import React, { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { getItemWithExpiration } from '../../lib/utils';
import { PeriodNavigatorType } from '../Date/PeriodNavigator';
import MemberClassHistoryComponentsHeader from './MemberClassHistoryComponentsHeader';
import MemberClassHistoryComponentsGrid from './MemberClassHistoryComponentsGrid';
import { MemberClassHistorySchedule, MonthlySchedule, YearSchedule } from '../../services/useCenterQueryService';
import { useQuery } from '@tanstack/react-query';
import { getMemberScheduleHistory } from '../../api/center';

type Props = {
    coachId?: string;
    coachIds?: string[];
    memberId?: string;
    onClickRow?: (row: MemberClassHistorySchedule) => void;
    hideTitle?: boolean;
    coachMap?: Record<string, string>;
};

const MemberClassHistoryComponents = React.memo(({ coachId, coachIds, memberId, onClickRow, hideTitle, coachMap }: Props) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const [classHistoryDate, setClassHistoryDate] = useState<string>(dayjs().toISOString());

    // Use the first available coachId — the API returns all lessons for this member within the requested center.
    const effectiveCoachId = coachIds?.[0] || coachId;

    const [historyData, setHistoryData] = useState<MonthlySchedule | undefined>(undefined);
    const [monthlyData, setMonthlyData] = useState<MemberClassHistorySchedule[] | undefined>([]);
    const [yearlyData, setYearlyData] = useState<YearSchedule | undefined>({});
    const [totalData, setTotalData] = useState<YearSchedule[] | undefined>([]);
    const _ = useQuery(
        ['getMemberScheduleHistoryQuery', authObject?.centerId, effectiveCoachId, memberId, classHistoryDate],
        async () =>
            await getMemberScheduleHistory(
                authObject?.centerId,
                effectiveCoachId,
                memberId,
                dayjs(classHistoryDate).format('YYYY-MM-DD'),
                authorization
            ),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    // 통합 배열 (B안) 우선 — unified가 없으면 PT 필드로 fallback (하위 호환)
                    const thisMonth: any = res.data.thisMonthAll ?? res.data.thisMonth;
                    const thisYear = res.data.thisYearAll ?? res.data.thisYear;
                    const entire = res.data.totalViewAll ?? res.data.totalView;

                    setHistoryData(res.data);

                    if (thisMonth) {
                        setMonthlyData(thisMonth);
                    }

                    if (thisYear) {
                        setYearlyData(thisYear);
                    }

                    if (entire) {
                        setTotalData(entire);
                    }
                }
            },

            enabled: !!authorization && !!authObject?.centerId && !!effectiveCoachId && !!memberId,
            staleTime: 0,
        }
    );

    const [type, setType] = useState<PeriodNavigatorType>('thisMonth');

    const onResetPeriod = useCallback(() => {
        setType('thisMonth');
        setClassHistoryDate(dayjs().toISOString());
    }, []);
    const onClickPrev = useCallback(
        (type: PeriodNavigatorType) => {
            switch (type) {
                case 'thisMonth':
                    setClassHistoryDate(dayjs(classHistoryDate).subtract(1, 'month').toISOString());
                    break;
                case 'thisYear':
                    setClassHistoryDate(dayjs(classHistoryDate).subtract(1, 'year').toISOString());
                    break;
                default:
                    break;
            }
        },
        [classHistoryDate]
    );

    const onClickNext = useCallback(
        (type: PeriodNavigatorType) => {
            switch (type) {
                case 'thisMonth':
                    setClassHistoryDate(dayjs(classHistoryDate).add(1, 'month').toISOString());
                    break;
                case 'thisYear':
                    setClassHistoryDate(dayjs(classHistoryDate).add(1, 'year').toISOString());
                    break;
                default:
                    break;
            }
        },
        [classHistoryDate]
    );

    // 통합 카운트 우선, 없으면 PT 카운트로 fallback
    const monthLength = historyData?.thisMonthAllCount ?? historyData?.thisMonthScheduleCount;
    const yearLength = historyData?.thisYearAllCount ?? historyData?.thisYearScheduleCount;
    const allLength = historyData?.allCount ?? historyData?.allScheduleCount;

    return (
        <div className="flex flex-col w-full h-full">
            <MemberClassHistoryComponentsHeader
                currentDate={classHistoryDate}
                onClickPrev={onClickPrev}
                onClickNext={onClickNext}
                onClickType={(type) => setType(type)}
                type={type}
                monthLength={monthLength}
                yearLength={yearLength}
                allLength={allLength}
                hideTitle={hideTitle}
                onResetPeriod={onResetPeriod}
            />
            <MemberClassHistoryComponentsGrid
                type={type}
                currentDate={classHistoryDate}
                monthlyData={monthlyData}
                yearlyData={yearlyData}
                totalData={totalData}
                onClickRow={onClickRow}
                coachMap={coachMap}
            />
        </div>
    );
});

export default MemberClassHistoryComponents;
